import { Repetition, Unit, WorkingTimeClassicType } from "../../__generated__/graphql";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDurationFormatter, useI18n } from "../../hooks/i18n-hooks";
import { OptionType } from "../OptionsSelector";
import { DEFAULT_HOURS_PER_WEEK, MINIMUM_SLOT_DURATION_HOURS } from "../../utils";
import VStack from "../../ui_atoms/VStack";
import MonthlyTimePerWeekClassicComponent from "./MonthlyTimePerWeekClassicComponent";
import MonthlyPercentClassicComponent from "./MonthlyPercentClassicComponent";
import Gap from "../../ui_atoms/Gap";

import { setContractWorkingTimeClassicSpec, WorkingTimeComponentProps } from "./helpers";
import HoursPerWeekSection from "./HoursPerWeekSection";
import { BodyMediumText, BodySmallText } from "../FormattedText";
import { useTheme } from "../../hooks/use-theme";
import DropdownField from "../DropdownField";
import { View } from "react-native";

export type ClassicModeType = "percent" | "timePerWeek";

const MonthlyClassicComponent: React.FC<WorkingTimeComponentProps> = ({
    disabled = false,
    contract,
    setContract,
}) => {
    const { colors } = useTheme();
    const i18n = useI18n();
    const setter = useCallback(
        (v: { workingTime: WorkingTimeClassicType; percentage?: number }) =>
            setContract({
                ...setContractWorkingTimeClassicSpec(contract, v.workingTime),
                workingPercentage: v.percentage,
            }),
        [setContract, contract],
    );
    const [classicMode, setClassicMode] = useState<ClassicModeType>("percent");

    const onTimeModeChange = useCallback(
        (mode: ClassicModeType) => {
            setClassicMode(mode);
        },
        [setClassicMode],
    );

    const timeModeData = useMemo(
        () =>
            [
                {
                    label: i18n.t("utils.working_time.classic.modes.percent"),
                    value: "percent",
                },
                {
                    label: i18n.t("utils.working_time.classic.modes.timePerWeek"),
                    value: "timePerWeek",
                },
            ] as [OptionType<ClassicModeType>, OptionType<ClassicModeType>],
        [i18n],
    );

    useEffect(() => {
        if (!contract.workingTimeClassicSpec) {
            setContract({
                ...contract,
                workingTimeClassicSpec: {
                    repetition: Repetition.Week,
                    unitCount: contract.hoursPerWeekFullTime || DEFAULT_HOURS_PER_WEEK,
                    unit: Unit.Hours,
                },
            });
        }
    }, [contract, setContract]);
    const formatDuration = useDurationFormatter();

    if (!contract.workingTimeClassicSpec) {
        return null;
    }
    const hoursPerWeek = contract?.hoursPerWeekFullTime || 0;
    const percent = hoursPerWeek
        ? (contract.workingTimeClassicSpec.unitCount || 0) / hoursPerWeek
        : 0;
    const percentValue = percent ? Math.round(percent * 10000) / 100.0 : 0;
    return (
        <VStack>
            <BodyMediumText>{i18n.t("utils.working_time.monthly_hours_or_percent")}</BodyMediumText>
            <Gap gap={1} />
            <View style={{ flexDirection: "row" }}>
                {classicMode === "timePerWeek" ? (
                    <MonthlyTimePerWeekClassicComponent
                        onChange={setter}
                        workingTime={contract.workingTimeClassicSpec as WorkingTimeClassicType}
                        disabled={disabled}
                        hoursPerWeek={contract.hoursPerWeekFullTime || DEFAULT_HOURS_PER_WEEK}
                        error={
                            !contract.workingTimeClassicSpec?.unitCount ||
                            contract.workingTimeClassicSpec.unitCount < MINIMUM_SLOT_DURATION_HOURS
                                ? i18n.t(
                                      "screens.user.create_mission_working_time_more.minimum_duration_error",
                                  )
                                : undefined
                        }
                    />
                ) : (
                    <MonthlyPercentClassicComponent
                        onChange={setter}
                        workingTime={contract.workingTimeClassicSpec as WorkingTimeClassicType}
                        disabled={disabled}
                        hoursPerWeek={contract.hoursPerWeekFullTime || DEFAULT_HOURS_PER_WEEK}
                    />
                )}
                <Gap gap={0.25} />
                <DropdownField<ClassicModeType>
                    label={i18n.t("utils.working_time.units")}
                    numberOfLines={1}
                    style={{ flexShrink: 1, flexGrow: 0, width: "auto" }}
                    items={timeModeData}
                    onSelect={onTimeModeChange}
                    value={classicMode}
                />
            </View>
            <Gap gap={0.25} />
            {contract.workingTimeClassicSpec?.unitCount && percentValue ? (
                <BodySmallText color={colors.onSurfaceDisabled}>
                    {i18n.t("utils.working_time.per_week_value", {
                        hours: formatDuration(
                            (contract.workingTimeClassicSpec?.unitCount || 0) * 60,
                        ),
                    })}{" "}
                    {`[${percentValue}%]`}
                </BodySmallText>
            ) : null}
            <Gap gap={1} />
            <HoursPerWeekSection
                contract={contract}
                setContract={setContract}
                disabled={disabled}
                mode={classicMode}
            />
        </VStack>
    );
};

export default MonthlyClassicComponent;
